import React, { useCallback } from 'react';
import Button from '../../styled/Button';
import Distance from '../../styled/Distance';
import { theme } from '../../styled/layout/theme';
import { StyledLink } from '../../styled/Link';
import Panel from '../../styled/Panel';
import CookieBanner from './CookieBanner';
import Text from '../../styled/Text/index';
import styled from 'styled-components';
import { size } from '../../styled/layout/helpers';
import CookieOptions from './CookieOptions';
import Link from '../../Router/Link';
import useInitialRender from '../../hooks/useInitialRender';
import useUpdateCookies from './useUpdateCookies';
import useCookieConsentPanel from '../../hooks/useCookieConsentPanel';
import { test_ids } from '../../../tests/test_ids';

export const EV_COOKIE_CONSENT_UPDATED = 'EV_COOKIE_CONSENT_UPDATED';

export interface ICookie {
  name: string;
  displayName: string;
  optional: boolean;
  active: boolean;
}

const Container = styled.div`
  justify-content: space-between;
  display: flex;
  flex-direction: row;
  margin-top: ${size(2)};
  align-items: center;

  ${theme.screens.LtMd} {
    flex-direction: column;
  }

  ${theme.screens.LtSm} {
    align-items: inherit;
    font-size: 1rem;
    display: flex;
    flex-direction: column;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${size(1)};
  flex-wrap: wrap;

  ${theme.screens.LtSm} {
    flex-direction: column;
  }

  ${theme.screens.LtSm} {
    display: flex;
    gap: ${size(2)};
    justify-content: space-between;
    button {
      padding: ${size(1)} !important;
    }
  }
`;

const CookieConsent = () => {
  const isInitialRender = useInitialRender(true);

  const { allowAll, consenting, setSettings, draftSettings, setDraftSettings, allowOnlyNecessary } =
    useUpdateCookies();
  const cookieConsentPanel = useCookieConsentPanel();

  const handleApproveAll = useCallback(() => {
    allowAll();
    cookieConsentPanel.hide();
  }, [cookieConsentPanel]);
  const handleSaveSettings = useCallback(() => {
    setSettings(draftSettings);
    cookieConsentPanel.hide();
  }, [draftSettings, cookieConsentPanel]);
  const handleOnlyNecessary = useCallback(() => {
    allowOnlyNecessary();
    cookieConsentPanel.hide();
  }, [cookieConsentPanel]);

  if (SSR || isInitialRender()) {
    return null;
  }

  return (
    <>
      {!consenting && (
        <CookieBanner title="Cookies">
          <Text>
            Vi använder cookies för att anpassa innehåll, annonser och för att analysera vår trafik.
            Vi delar också information om din användning av vår webbplats med våra annons- och
            analyspartners som kan kombinera den med annan information som du har tillhandahållit
            dem eller som de har samlat in från din användning av deras andra tjänster.
          </Text>
          <Container>
            <Link
              Component={StyledLink}
              href={'/kundservice/hjalp/dataskydd/cookies-policy'}
              title={`Läs mer om cookies`}>
              Läs mer om hur vi hanterar cookies här {'>'}
            </Link>
            <Distance size={'1'} />
            <ApproveButtons
              onPrimaryClick={handleApproveAll}
              onSecondaryClick={cookieConsentPanel.show}
              textPrimary="Godkänn alla"
              textSecondary="Anpassa val"
              onOnlyNecessaryClick={handleOnlyNecessary}
              secondaryTestId={test_ids.cookies.customizeCookies}
            />
          </Container>
        </CookieBanner>
      )}

      {cookieConsentPanel.isVisible && (
        <Panel
          zIndex={300}
          onClose={cookieConsentPanel.hide}
          title="Cookies"
          data-testid={test_ids.cookies.cookiePanel}>
          <Text>
            Vi använder cookies för att anpassa innehåll, annonser och för att analysera vår trafik.
            Vi delar också information om din användning av vår webbplats med våra annons- och
            analyspartners som kan kombinera den med annan information som du har tillhandahållit
            dem eller som de har samlat in från din användning av deras andra tjänster. Genom att
            klicka på ”Godkänn alla val” godkänner du användandet av cookies. Du kan när som helst
            justera användningen av cookies här i din webbläsare eller på vår sida om cookies. Att
            blockera vissa typer av cookies kan påverka din upplevelse av webbplatsen och de
            tjänster som vi erbjuder. Gör dina val här nedanför och klicka sedan på ”Spara mina val"
          </Text>
          <Distance size={'2'} />
          <Link
            Component={StyledLink}
            onClick={cookieConsentPanel.hide}
            href={'/kundservice/hjalp/dataskydd/cookies-policy'}
            title={`Läs mer om cookies`}>
            Läs mer om cookies här {'>'}
          </Link>
          <Distance size={'2'} />
          <CookieOptions cookies={draftSettings} setCookies={setDraftSettings} />
          <Distance size={'2'} />
          <ApproveButtons
            onPrimaryClick={handleApproveAll}
            onSecondaryClick={handleSaveSettings}
            textPrimary="Godkänn alla val"
            textSecondary="Spara mina val"
            secondaryTestId={test_ids.saveSettings}
          />
        </Panel>
      )}
    </>
  );
};

export default CookieConsent;

interface IApproveButtons {
  onSecondaryClick: () => void;
  textSecondary: string;
  onPrimaryClick: () => void;
  textPrimary: string;
  onOnlyNecessaryClick?: () => void;
  secondaryTestId?: string;
}

const ApproveButtons = ({
  onSecondaryClick,
  textSecondary,
  onPrimaryClick,
  textPrimary,
  onOnlyNecessaryClick,
  secondaryTestId,
}: IApproveButtons) => {
  return (
    <ButtonContainer>
      <Button
        size="small"
        tone="ghost"
        style={{ flex: 1 }}
        onClick={onSecondaryClick}
        rounded
        data-testid={secondaryTestId || test_ids.cookies.customizeCookies}>
        {textSecondary}
      </Button>
      {/* onlyNecessary should only be vissible for cookie banner */}
      {onOnlyNecessaryClick && (
        <Button
          size="small"
          style={{ flex: 1, backgroundColor: theme.colors.button.base.default }}
          onClick={onOnlyNecessaryClick}
          data-testid={test_ids.cookies.onlyNecessaryCookies}
          rounded>
          Endast nödvändiga
        </Button>
      )}
      <Button
        size="small"
        style={{ flex: 1, backgroundColor: theme.colors.button.base.default }}
        onClick={onPrimaryClick}
        data-testid={test_ids.cookies.acceptAllCookies}
        rounded>
        {textPrimary}
      </Button>
    </ButtonContainer>
  );
};
